import { supabase } from '@/integrations/supabase/client';

/**
 * Os números que são NOSSOS — as linhas de WhatsApp do CRM.
 *
 * Existe porque em 05/08/2026 encontramos 131 leads com o telefone de uma
 * conexão da casa gravado no campo "Contato Real" do cliente, 96 deles já
 * convertidos em pedido. O efeito prático: a proposta saía do CRM e chegava no
 * próprio consultor, o que satisfazia as duas exigências para criar o pedido
 * (telefone válido + interação no WhatsApp) sem o cliente receber nada. Nos
 * casos mais rápidos, o pedido nascia 5 a 8 segundos depois do "envio".
 *
 * Douglas 05/08: "a proposta tem que ir para o cliente" e "não ter como colocar
 * nenhum número nosso".
 *
 * Compara pelo sufixo de 8 dígitos: é o que sobrevive a DDI, DDD e ao nono
 * dígito entrando ou saindo entre um cadastro e outro.
 */

type ConsultaConexoes = {
  select: (colunas: string) => Promise<{ data: { telefone: string | null; whatsapp_number?: string | null }[] | null }>;
};

const conexoes = supabase as unknown as { from: (tabela: string) => ConsultaConexoes };

const VALIDADE_CACHE_MS = 10 * 60 * 1000;
let cache: { sufixos: Set<string>; em: number } | null = null;

/** Últimos 8 dígitos — ignora DDI, DDD e o nono dígito */
export function sufixoTelefone(valor?: string | null): string | null {
  const digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length < 10) return null;
  return digitos.slice(-8);
}

/** Sufixos de todas as linhas de WhatsApp do CRM (inclusive as desativadas — número nosso continua nosso) */
export async function sufixosDaCasa(): Promise<Set<string>> {
  if (cache && Date.now() - cache.em < VALIDADE_CACHE_MS) return cache.sufixos;

  const [{ data: linhas }, { data: usuarios }] = await Promise.all([
    conexoes.from('whatsapp_connections').select('telefone'),
    conexoes.from('usuarios').select('telefone, whatsapp_number'),
  ]);
  const sufixos = new Set<string>();
  for (const registro of [...(linhas || []), ...(usuarios || [])]) {
    for (const telefone of [registro?.telefone, registro?.whatsapp_number]) {
      const s = sufixoTelefone(telefone);
      if (s) sufixos.add(s);
    }
  }

  // Sem resposta do banco NÃO cacheamos: cachear vazio desligaria a trava por
  // 10 minutos a cada falha de rede, e é exatamente nela que a trava importa.
  if (sufixos.size > 0) cache = { sufixos, em: Date.now() };
  return sufixos;
}

/** true quando o telefone é uma linha nossa (não pode ser contato de cliente) */
export async function ehNumeroDaCasa(telefone?: string | null): Promise<boolean> {
  const sufixo = sufixoTelefone(telefone);
  if (!sufixo) return false;
  const daCasa = await sufixosDaCasa();
  return daCasa.has(sufixo);
}

/** Nome da conexão dona do número, para a mensagem de erro dizer qual linha é */
export async function nomeDaConexaoDoNumero(telefone?: string | null): Promise<string | null> {
  const sufixo = sufixoTelefone(telefone);
  if (!sufixo) return null;
  const conexoesComNome = supabase as unknown as {
    from: (tabela: string) => { select: (colunas: string) => Promise<{ data: { nome: string | null; telefone: string | null }[] | null }> };
  };
  const { data } = await conexoesComNome.from('whatsapp_connections').select('nome, telefone');
  const achou = (data || []).find((c) => sufixoTelefone(c?.telefone) === sufixo);
  return achou?.nome ?? null;
}

export const MENSAGEM_NUMERO_DA_CASA =
  'Este telefone é uma linha do próprio CRM, não do cliente. O Contato Real precisa ser o número do cliente — a proposta tem que chegar nele.';
